import * as React from "react"
import {useState, useCallback} from "react"
import {Avatar, Tooltip, Menu, MenuItem, Box, Typography, CircularProgress} from "@mui/material"
import {useAppToast} from "@ui-seeker/app"
import {postSubmit} from "@ui-seeker/utils"

import {BACKEND_APIS} from "@constants"
import {batchesTableData, isLoadingBatches} from "../@signals"


// Get initials from user name
const getInitials = (name) => {
	if (!name) return '?'
	const parts = name.trim().split(' ')
	if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Generate color from string
const stringToColor = (string) => {
	if (!string) return '#757575'
	let hash = 0
	for (let i = 0; i < string.length; i++) {
		hash = string.charCodeAt(i) + ((hash << 5) - hash)
	}
	const hue = hash % 360
	return `hsl(${hue}, 60%, 50%)`
}


export default function UserCell({batchID, userName, userID, status, users}) {
	const {showToast} = useAppToast()
	const [anchorEl, setAnchorEl] = useState(null)
	const [isUpdating, setIsUpdating] = useState(false)
	const open = Boolean(anchorEl)

	// Only allow user assignment when batch is in 'ready' state
	const canAssignUser = status === 'ready'
	const isBeforeReady = status === 'creating' || status === 'initialization'
	const isFinished = status === 'finished'

	const handleClick = useCallback((event) => {
		if (!canAssignUser) return
		setAnchorEl(event.currentTarget)
	}, [canAssignUser])

	const handleClose = useCallback(() => {
		setAnchorEl(null)
	}, [])

	const handleSelectUser = useCallback(async (selectedUserID, selectedUserName) => {
		handleClose()
		setIsUpdating(true)

		try {
			await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}/assign-user`, {
				userID: selectedUserID
			})

			// Update the table data
			batchesTableData.value = batchesTableData.value.map(batch => 
				batch.batchID === batchID 
					? {...batch, userID: selectedUserID, userName: selectedUserName}
					: batch
			)

			showToast.success(`Batch assigned to ${selectedUserName}`)
		} catch (err) {
			showToast.error('Failed to assign user')
		} finally {
			setIsUpdating(false)
		}
	}, [batchID, handleClose, showToast])

	const handleUnassign = useCallback(async () => {
		handleClose()
		setIsUpdating(true)

		try {
			await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}/assign-user`, {
				userID: null
			})

			// Update the table data
			batchesTableData.value = batchesTableData.value.map(batch => 
				batch.batchID === batchID 
					? {...batch, userID: null, userName: null}
					: batch
			)

			showToast.success('User unassigned from batch')
		} catch (err) {
			showToast.error('Failed to unassign user')
		} finally {
			setIsUpdating(false)
		}
	}, [batchID, handleClose, showToast])

	if (isUpdating) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
				<CircularProgress size={32} />
			</Box>
		)
	}

	// Get tooltip text based on status
	const getTooltipText = () => {
		if (isBeforeReady) return 'User assignment not available yet'
		if (isFinished) return userName ? `Assigned to ${userName} (finished)` : 'No user assigned (finished)'
		if (!userName) return 'Click to assign user'
		return userName
	}

	return (
		<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
			<Tooltip title={getTooltipText()}>
				<Avatar
					onClick={handleClick}
					sx={{
						width: 32,
						height: 32,
						fontSize: '0.875rem',
						bgcolor: userName ? stringToColor(userName) : '#e0e0e0',
						color: userName ? '#fff' : '#757575',
						cursor: canAssignUser ? 'pointer' : 'not-allowed',
						opacity: canAssignUser ? 1 : 0.6,
						'&:hover': canAssignUser ? {
							opacity: 0.8,
							transform: 'scale(1.05)'
						} : {},
						transition: 'all 0.2s'
					}}
				>
					{getInitials(userName)}
				</Avatar>
			</Tooltip>

			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				PaperProps={{
					sx: { maxHeight: 300, width: 250 }
				}}
			>
				{userID && (
					<MenuItem onClick={handleUnassign} sx={{ color: 'error.main' }}>
						<Typography variant="body2">Unassign User</Typography>
					</MenuItem>
				)}
				{userID && <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 1 }} />}
				{users && users.length > 0 ? (
					users.map(user => (
						<MenuItem 
							key={user.userID} 
							onClick={() => handleSelectUser(user.userID, user.userName)}
							selected={user.userID === userID}
						>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Avatar
									sx={{
										width: 24,
										height: 24,
										fontSize: '0.75rem',
										bgcolor: stringToColor(user.userName)
									}}
								>
									{getInitials(user.userName)}
								</Avatar>
								<Typography variant="body2">{user.userName}</Typography>
							</Box>
						</MenuItem>
					))
				) : (
					<MenuItem disabled>
						<Typography variant="body2" color="text.secondary">No users available</Typography>
					</MenuItem>
				)}
			</Menu>
		</Box>
	)
}
